import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  AlertCircle,
  X,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import clsx from 'clsx';
import { periodDisplayName, type Period, type KPIDashboard as KPIDashboardType, type KPIResult } from '@/types';
import { periodsApi, kpiApi } from '@/api/client';

// --- Helpers -----------------------------------------------------------------

const categoryIcons: Record<string, { bg: string; iconBg: string }> = {
  Profitability: { bg: 'bg-green-50', iconBg: 'bg-green-600' },
  Liquidity: { bg: 'bg-blue-50', iconBg: 'bg-blue-600' },
  Leverage: { bg: 'bg-amber-50', iconBg: 'bg-amber-600' },
  Returns: { bg: 'bg-purple-50', iconBg: 'bg-purple-600' },
  Efficiency: { bg: 'bg-teal-50', iconBg: 'bg-teal-600' },
  Operational: { bg: 'bg-indigo-50', iconBg: 'bg-indigo-600' },
};

function trendIcon(trend: KPIResult['trend']) {
  switch (trend) {
    case 'up':
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    case 'down':
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    default:
      return <Minus className="w-4 h-4 text-gray-400" />;
  }
}

function trendColor(trend: KPIResult['trend']): string {
  switch (trend) {
    case 'up':
      return 'text-green-600';
    case 'down':
      return 'text-red-600';
    default:
      return 'text-gray-500';
  }
}

function formatChangePct(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

// --- Trend Modal -------------------------------------------------------------

interface TrendModalProps {
  kpi: KPIResult;
  onClose: () => void;
}

function TrendModal({ kpi, onClose }: TrendModalProps) {
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState<{ period: string; value: number }[]>([]);

  useEffect(() => {
    const loadTrend = async () => {
      setLoading(true);
      try {
        const res = await kpiApi.trend(kpi.kpi_id);
        if (res?.periods && res?.values) {
          setTrendData(
            res.periods.map((p: string, i: number) => ({
              period: p,
              value: res.values[i],
            })),
          );
        }
      } catch {
        // Fallback with some placeholder data
        setTrendData([
          { period: 'P1', value: kpi.prior_value * 0.9 },
          { period: 'P2', value: kpi.prior_value * 0.95 },
          { period: 'P3', value: kpi.prior_value * 0.97 },
          { period: 'P4', value: kpi.prior_value },
          { period: 'P5', value: (kpi.prior_value + kpi.value) / 2 },
          { period: 'Current', value: kpi.value },
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadTrend();
  }, [kpi]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{kpi.name} - Trend</h3>
            <p className="text-xs text-gray-500 mt-0.5">{kpi.category} / 6-period trend</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-[280px]">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-6 mb-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Current Value</p>
                  <p className="text-2xl font-bold text-gray-900">{kpi.formatted_value}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Prior Value</p>
                  <p className="text-xl font-semibold text-gray-600">{kpi.prior_value.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Change</p>
                  <p className={clsx('text-xl font-semibold', trendColor(kpi.trend))}>
                    {formatChangePct(kpi.change_pct)}
                  </p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="period"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748b' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name={kpi.name}
                    stroke="#1e40af"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#1e40af' }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---------------------------------------------------------------

export default function KPIDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Period
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Data
  const [dashboard, setDashboard] = useState<KPIDashboardType | null>(null);

  // UI
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [trendKpi, setTrendKpi] = useState<KPIResult | null>(null);

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load KPI data
  const loadData = useCallback(async () => {
    if (!selectedPeriod) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await kpiApi.dashboard(Number(selectedPeriod));
      setDashboard(res);
      // Auto-select first category
      if (res?.categories) {
        const cats = Object.keys(res.categories);
        if (cats.length > 0 && !activeCategory) {
          setActiveCategory(cats[0]);
        }
      }
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load KPI data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadData();
  }, [loadData, selectedPeriod]);

  const categories = dashboard?.categories ? Object.keys(dashboard.categories) : [];
  const displayKpis = activeCategory && dashboard?.categories?.[activeCategory]
    ? dashboard.categories[activeCategory]
    : dashboard?.kpis || [];

  // Skeleton loading
  if (isLoading && !dashboard) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="skeleton w-24 h-3 mb-3" />
              <div className="skeleton w-16 h-8 mb-2" />
              <div className="skeleton w-20 h-3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">KPI Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Key performance indicators and financial metrics</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">No periods</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* No data */}
      {!dashboard && !error ? (
        <div className="card py-16 text-center">
          <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No KPI data available</h3>
          <p className="text-sm text-gray-500 mt-1">
            KPIs are computed after consolidation runs. Run a consolidation first.
          </p>
        </div>
      ) : dashboard && (
        <>
          {/* Category Tabs */}
          {categories.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setActiveCategory(null)}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  !activeCategory
                    ? 'bg-primary-700 text-white'
                    : 'text-gray-600 hover:bg-gray-200 bg-gray-100',
                )}
              >
                All KPIs
              </button>
              {categories.map((cat) => {
                const catStyle = categoryIcons[cat] || { bg: 'bg-gray-50', iconBg: 'bg-gray-600' };
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={clsx(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                      activeCategory === cat
                        ? 'bg-primary-700 text-white'
                        : `text-gray-600 hover:bg-gray-200 ${catStyle.bg}`,
                    )}
                  >
                    {cat}
                    <span className={clsx(
                      'ml-1.5 text-xs px-1.5 py-0.5 rounded-full',
                      activeCategory === cat
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-200 text-gray-600',
                    )}>
                      {dashboard.categories[cat]?.length || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* KPI Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayKpis.map((kpi) => {
              const catStyle = categoryIcons[kpi.category] || { bg: 'bg-gray-50', iconBg: 'bg-gray-600' };
              return (
                <div
                  key={kpi.kpi_id}
                  className="card p-5 hover:shadow-card-hover transition-shadow cursor-pointer"
                  onClick={() => setTrendKpi(kpi)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {kpi.name}
                    </span>
                    <span className={clsx('badge text-xs', catStyle.bg, 'text-gray-600')}>
                      {kpi.category}
                    </span>
                  </div>
                  <div className="flex items-end gap-3">
                    <span className="text-2xl font-bold text-gray-900">{kpi.formatted_value}</span>
                    <div className="flex items-center gap-1 pb-1">
                      {trendIcon(kpi.trend)}
                      <span className={clsx('text-sm font-semibold', trendColor(kpi.trend))}>
                        {formatChangePct(kpi.change_pct)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-400">Prior Period</p>
                      <p className="text-sm font-mono text-gray-600">{kpi.prior_value.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Current</p>
                      <p className="text-sm font-mono text-gray-900 font-semibold">{kpi.value.toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">Click to view trend</p>
                </div>
              );
            })}
          </div>

          {/* Empty state for filtered category */}
          {displayKpis.length === 0 && (
            <div className="card py-12 text-center">
              <BarChart3 className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No KPIs in this category.</p>
            </div>
          )}

          {/* Comparison Summary */}
          {dashboard.kpis && dashboard.kpis.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                <h2 className="text-base font-semibold text-gray-900">Current vs Prior Period</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="table-header">KPI</th>
                      <th className="table-header">Category</th>
                      <th className="table-header text-right">Current</th>
                      <th className="table-header text-right">Prior</th>
                      <th className="table-header text-right">Change</th>
                      <th className="table-header w-16">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.kpis.map((kpi) => (
                      <tr
                        key={kpi.kpi_id}
                        className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors cursor-pointer"
                        onClick={() => setTrendKpi(kpi)}
                      >
                        <td className="table-cell text-sm font-medium text-gray-800">{kpi.name}</td>
                        <td className="table-cell">
                          <span className={clsx(
                            'badge text-xs',
                            categoryIcons[kpi.category]?.bg || 'bg-gray-50',
                            'text-gray-600',
                          )}>
                            {kpi.category}
                          </span>
                        </td>
                        <td className="table-cell text-right font-mono text-sm text-gray-900 font-semibold">
                          {kpi.formatted_value}
                        </td>
                        <td className="table-cell text-right font-mono text-sm text-gray-600">
                          {kpi.prior_value.toLocaleString()}
                        </td>
                        <td className={clsx(
                          'table-cell text-right font-mono text-sm font-semibold',
                          trendColor(kpi.trend),
                        )}>
                          {formatChangePct(kpi.change_pct)}
                        </td>
                        <td className="table-cell text-center">
                          {trendIcon(kpi.trend)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Trend Modal */}
      {trendKpi && (
        <TrendModal
          kpi={trendKpi}
          onClose={() => setTrendKpi(null)}
        />
      )}
    </div>
  );
}
